/**
 * The rep's payment schedule, frozen for the customer's document.
 *
 * The builder lays a schedule out live (`paymentSchedule`); the customer's
 * proposal needs the schedule as it stood when the rep sent it, so what they
 * read and sign cannot move under them (ADR 0032). This file is the shape it
 * is frozen in, and the question the proposal asks of it: did the rep write
 * this, or is it the standard split (ADR 0267)?
 */

import type { Cents } from "./money.js";
import {
  FINAL_SLOT,
  TRIGGER_ON_COMPLETION,
  TRIGGER_ON_SIGNING,
  typedPayment,
  type StoredPayment,
} from "./paymentSchedule.js";

/**
 * One slot of a schedule as the customer's document freezes it.
 *
 * Cents, not the dollars `estimate_payments.amount` is stored in, because
 * everything else a presented document carries is cents. The Final slot
 * always freezes `amount: null` — it is the remainder, worked out on the
 * customer's phone from what they chose (ADR 0031) — but its wording is
 * frozen like the others, because "Paid in Increments" is a thing a rep
 * writes there.
 */
export interface FrozenPayment {
  slot: number;
  amount: Cents | null;
  triggerText: string | null;
}

/** The wording a slot carries when the rep has not written their own. */
export function defaultTriggerText(slot: number): string {
  if (slot === 1) return TRIGGER_ON_SIGNING;
  if (slot === FINAL_SLOT) return TRIGGER_ON_COMPLETION;
  return "";
}

/**
 * The rep's schedule, frozen for the customer's document: the three typed
 * amounts (null for "nothing typed") and the wording on all four slots.
 */
export function frozenPayments(
  payments: readonly StoredPayment[],
): FrozenPayment[] {
  return [1, 2, 3, FINAL_SLOT].map((slot) => {
    const row = payments.find((p) => Number(p.slot) === slot);
    const text = row?.trigger_text?.trim();
    return {
      slot,
      amount: slot === FINAL_SLOT ? null : typedPayment(payments, slot),
      triggerText: text ? text : null,
    };
  });
}

/**
 * Whether the rep wrote this schedule themselves.
 *
 * Any amount typed into First, Second or Third — zero included, because zero
 * is a decision — or wording of their own on any slot. A slot with no wording
 * reads as the stock line (`defaultTriggerText` fills it in), so an estimate
 * left on the standard split is not custom, and the proposal prints its
 * deposit sentence alone, as it always has (ADR 0267).
 */
export function paymentsAreCustom(
  frozen: readonly FrozenPayment[] | null | undefined,
): boolean {
  if (!frozen) return false;
  return frozen.some((row) => {
    if (row.slot !== FINAL_SLOT && row.amount != null) return true;
    const text = row.triggerText ?? "";
    return text !== "" && text !== defaultTriggerText(row.slot);
  });
}
